import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Video, BookOpen, ArrowRight, Download, Loader2, Search, FlaskConical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageContainer } from "@/components/layouts/MainLayout";
import { T } from "@/components/editable/T";
import { isUpcomingWebinar } from "@/lib/webinarDate";
import { getPayTransparencyAssessmentPath } from "@/lib/routeUtils";
import { resolveContentLink } from "@/lib/contentLink";

type ResourceType = "all" | "webinar" | "template" | "eguide" | "scan" | "research";

interface Resource {
  id: string;
  type: "webinar" | "template" | "eguide" | "scan" | "research";
  slug: string;
  title_nl: string;
  title_en: string | null;
  title_de: string | null;
  description_nl: string | null;
  description_en: string | null;
  description_de: string | null;
  image_url: string | null;
  duration: string | null;
  date: string | null;
  featured: boolean;
  published: boolean;
  hubspot_form_id_en: string | null;
  hubspot_form_id_de: string | null;
}

const upcomingLabels: Record<string, string> = {
  nl: "Aankomend",
  en: "Upcoming",
  de: "Demnächst",
};

const PAGE = "knowledgeHub";

const filterOptions: { value: ResourceType; labelKey: string; defaultLabel: string; icon: React.ElementType }[] = [
  { value: "all", labelKey: "filters.all", defaultLabel: "Alles", icon: FileText },
  { value: "webinar", labelKey: "filters.webinars", defaultLabel: "Webinars", icon: Video },
  { value: "template", labelKey: "filters.templates", defaultLabel: "Templates", icon: FileText },
  { value: "eguide", labelKey: "filters.eguides", defaultLabel: "E-guides", icon: BookOpen },
  { value: "scan", labelKey: "filters.scans", defaultLabel: "Scans", icon: FileText },
  { value: "research", labelKey: "filters.research", defaultLabel: "Research", icon: FlaskConical },
];

const typeConfig: Record<string, { labels: Record<string, string>; color: string; textColor: string }> = {
  webinar: { labels: { nl: "Webinar", en: "Webinar", de: "Webinar" }, color: "#F3EDFD", textColor: "#9055EE" },
  template: { labels: { nl: "Template", en: "Template", de: "Vorlage" }, color: "#FDF2ED", textColor: "#EA580C" },
  eguide: { labels: { nl: "E-guide", en: "E-guide", de: "E-Guide" }, color: "#FFFBEB", textColor: "#CA8A04" },
  scan: { labels: { nl: "Scan", en: "Scan", de: "Scan" }, color: "#ECFDF5", textColor: "#059669" },
  research: { labels: { nl: "Research", en: "Research", de: "Research" }, color: "#EFF6FF", textColor: "#2563EB" },
};

const cardBackgrounds = ["#F3EDFD", "#FFFBEB", "#FDF2ED"];

const KnowledgeHub = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const [activeFilter, setActiveFilter] = useState<ResourceType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const currentLang = (i18n.language || lang || "nl") as "nl" | "en" | "de";

  const { data: resources = [], isLoading } = useQuery({
    queryKey: ["hr-resources-public"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("hr_resources")
        .select("id, slug, type, title_nl, title_en, title_de, description_nl, description_en, description_de, image_url, duration, date, featured, published, hubspot_form_id_en, hubspot_form_id_de")
        .eq("published", true)
        .order("sort_order");
      if (error) throw error;
      return data as Resource[];
    },
  });

  // Filter out resources not available in the current language
  const isAvailableInLang = (r: Resource) => {
    if (currentLang === "nl") return true;
    // Webinars are NL-only
    if (r.type === "webinar") return false;
    // Templates & e-guides need a form ID for the current language
    if (currentLang === "en") return !!r.hubspot_form_id_en;
    if (currentLang === "de") return !!r.hubspot_form_id_de;
    return true;
  };

  const availableResources = resources.filter(isAvailableInLang);
  const featuredResource = availableResources.find((r) => r.featured);

  const getTitle = (resource: Resource) => {
    if (currentLang === "en" && resource.title_en) return resource.title_en;
    if (currentLang === "de" && resource.title_de) return resource.title_de;
    return resource.title_nl;
  };

  const filteredResources = useMemo(() => {
    return availableResources.filter((r) => {
      if (r.featured) return false;
      if (activeFilter !== "all" && r.type !== activeFilter) return false;
      if (searchQuery.trim()) {
        const title = getTitle(r).toLowerCase();
        if (!title.includes(searchQuery.toLowerCase())) return false;
      }
      return true;
    });
  }, [availableResources, activeFilter, searchQuery, currentLang]);

  const getDescription = (resource: Resource) => {
    if (currentLang === "en" && resource.description_en) return resource.description_en;
    if (currentLang === "de" && resource.description_de) return resource.description_de;
    return resource.description_nl || "";
  };

  const getResourceUrl = (resource: Resource) => {
    // Special-case: assessment lives on its own page, not under /eguide/
    if (resource.slug === "wet-loonstransparantie-assessment") {
      return getPayTransparencyAssessmentPath(lang);
    }
    if (resource.slug === "quickscan-evaluatiecyclus") {
      return `/${lang}/quickscan-evaluatiecyclus/`;
    }
    const typePrefix =
      resource.type === "webinar" ? "webinar" :
      resource.type === "template" ? "template" :
      resource.type === "research" ? "research" : "eguide";
    return resolveContentLink(`/${lang}/${typePrefix}/${resource.slug}`, currentLang);
  };

  if (isLoading) {
    return (
      <section className="pt-[140px] pb-20 px-6">
        <PageContainer>
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </PageContainer>
      </section>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <section className="pt-[140px] pb-12 px-6 relative">
        <PageContainer>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                <a href={`/${currentLang}/`} className="hover:text-primary transition-colors">Home</a>
                <span>›</span>
                <span style={{ color: "#2A2334" }}>
                  <T page={PAGE} k="breadcrumb" d="HR Kenniscentrum" />
                </span>
              </div>

              <h1 className="mb-6" style={{ color: "#2A2334" }}>
                <T page={PAGE} k="hero.title" d="Maak je evaluaties slimmer en eerlijker met onze resources" />
              </h1>

              <p className="text-lead text-muted-foreground max-w-xl">
                <T page={PAGE} k="hero.subtitle" d="Ontdek onze handige templates, e-guides en webinars." />
              </p>
            </div>

            {featuredResource && (
              <div className="animate-fade-in" style={{ animationDelay: "200ms" }}>
                <Link
                  to={getResourceUrl(featuredResource)}
                  className="group block rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
                  style={{ border: "10px solid rgba(255, 255, 255, 0.7)" }}
                >
                  <div className="relative aspect-[16/9] overflow-hidden bg-background/60">
                    <img
                      src={featuredResource.image_url || "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=450&fit=crop"}
                      alt={getTitle(featuredResource)}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 flex gap-2">
                      {featuredResource.type === "webinar" && isUpcomingWebinar(featuredResource.date) && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: "#9055EE" }}>
                          {upcomingLabels[currentLang] || upcomingLabels.nl}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-white p-6">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 border" style={{ backgroundColor: "#FFFFFF", color: "#9055EE", borderColor: "#9055EE" }}>
                      {typeConfig[featuredResource.type].labels[currentLang] || typeConfig[featuredResource.type].labels.nl}
                    </span>
                    <h4 className="font-semibold mb-2 group-hover:text-primary transition-colors" style={{ color: "#2A2334" }}>
                      {getTitle(featuredResource)}
                    </h4>
                  </div>
                </Link>
              </div>
            )}
          </div>
        </PageContainer>
      </section>

      {/* Filter & Resources */}
      <section className="py-12 px-6">
        <PageContainer>
          <div className="flex flex-wrap items-center gap-3 mb-12">
            <div className="flex flex-wrap gap-3 flex-1">
              {filterOptions.map((option) => {
                const isActive = activeFilter === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => setActiveFilter(option.value)}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                      isActive ? "text-white shadow-md" : "bg-white text-muted-foreground hover:bg-gray-50 border border-border"
                    }`}
                    style={{ backgroundColor: isActive ? "#9055EE" : undefined }}
                  >
                    <option.icon className="w-4 h-4" />
                    <T page={PAGE} k={option.labelKey} d={option.defaultLabel} />
                  </button>
                );
              })}
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t(`${PAGE}.searchPlaceholder`, { defaultValue: "Zoeken..." }) as string}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 rounded-full border-border"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.map((resource, index) => (
              <Link
                key={resource.id}
                to={getResourceUrl(resource)}
                className="group flex flex-col rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-xl animate-fade-in"
                style={{ animationDelay: `${index * 100}ms`, border: "10px solid rgba(255, 255, 255, 0.7)", backgroundColor: cardBackgrounds[index % cardBackgrounds.length] }}
              >
                <div className="relative aspect-[2/1] overflow-hidden bg-background/60">
                  <img
                    src={resource.image_url || "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=450&fit=crop"}
                    alt={getTitle(resource)}
                    className="w-full h-full object-contain object-center transition-transform duration-700 group-hover:scale-105"
                  />
                  {resource.type === "webinar" && isUpcomingWebinar(resource.date) && (
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white shadow-md" style={{ backgroundColor: "#9055EE" }}>
                      {upcomingLabels[currentLang] || upcomingLabels.nl}
                    </div>
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                   <span className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 self-start border" style={{ backgroundColor: "#FFFFFF", color: "#9055EE", borderColor: "#9055EE" }}>
                     {typeConfig[resource.type].labels[currentLang] || typeConfig[resource.type].labels.nl}
                   </span>
                   <h5 className="font-semibold mb-2 group-hover:text-primary transition-colors flex-1" style={{ color: "#2A2334" }}>
                     {getTitle(resource)}
                   </h5>
                   <span className="inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 group-hover:gap-3 mt-auto" style={{ color: "#9055EE" }}>
                     {resource.type === "template" || resource.type === "eguide" ? (
                       <><T page={PAGE} k="download" d="Download" /><Download className="w-4 h-4" /></>
                     ) : (
                       <><T page={PAGE} k="view" d="Bekijken" /><ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></>
                     )}
                   </span>
                 </div>
              </Link>
            ))}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <T page={PAGE} k="noResources" d="Geen resources gevonden" />
            </div>
          )}
        </PageContainer>
      </section>
    </>
  );
};

export default KnowledgeHub;
